(() => {
  if (customElements.get('cahve-hero-carousel')) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  const MIN_DURATION_MS = 2000;

  class CahveHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      if (!this.slides.length) return;

      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.navButtons = Array.from(this.querySelectorAll('[data-carousel-nav]'));
      this.playPauseButton = this.querySelector('[data-carousel-play-pause]');
      this.status = this.querySelector('[data-carousel-status]');
      this.isMobile = window.matchMedia(MOBILE_QUERY);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.autoplayEnabled = this.dataset.autoplay === 'true' && !this.reduceMotion.matches;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.transition = this.dataset.transition || 'fade';
      this.desktopHeightMode = this.dataset.desktopHeightMode || 'fullscreen';
      this.mobileHeightMode = this.dataset.mobileHeightMode || 'fullscreen';
      this.defaultInterval = Math.max(Number(this.dataset.defaultInterval) || 6000, MIN_DURATION_MS);

      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.userPaused = false;
      this.hoverPaused = false;
      this.inViewport = this.isNearViewport();
      this.pointerGesture = null;
      this.timer = null;
      this.progressFrame = null;
      this.videoFrameRequest = null;
      this.currentSchedule = null;
      this.currentEndedHandler = null;
      this.currentEndedVideo = null;
      this._pendingPlayingVideo = null;
      this._pendingPlayingHandler = null;
      this.resizeTimer = null;
      this.preloadHandle = null;
      this.destroyCallbacks = [];

      this.bindEvents();
      this.observeVisibility();
      this.activateSlide(this.index, { announce: false });

      if (this.autoplayEnabled) {
        // Preserve any playback already started by the inline first-frame bootstrap.
        this.startCurrentPlayback({ resetMedia: false });
      } else {
        this.classList.remove('is-playing');
        this.updatePlayPauseButton();
        this.updateProgress(0);
      }
    }

    disconnectedCallback() {
      this.stopPlayback({ resetProgress: false, resetMedia: false });
      this.pauseAllVideos();
      this.cancelPreload();
      this.destroyCallbacks.forEach((callback) => callback());
      this.destroyCallbacks = [];
      this.intersectionObserver?.disconnect();
      window.clearTimeout(this.resizeTimer);
    }

    bindEvents() {
      const onPrev = () => this.goTo(this.getPreviousIndex(), { announce: true, restart: true, resetMedia: true });
      const onNext = () => this.goTo(this.getNextIndex(), { announce: true, restart: true, resetMedia: true });

      this.prevButton?.addEventListener('click', onPrev);
      this.nextButton?.addEventListener('click', onNext);
      this.destroyCallbacks.push(() => this.prevButton?.removeEventListener('click', onPrev));
      this.destroyCallbacks.push(() => this.nextButton?.removeEventListener('click', onNext));

      this.navButtons.forEach((button) => {
        const warmTarget = () => this.warmSlide(Number(button.dataset.carouselNav));
        const onClick = () => {
          const nextIndex = Number(button.dataset.carouselNav);
          this.goTo(nextIndex, { announce: true, restart: true, resetMedia: true });
        };

        button.addEventListener('pointerenter', warmTarget, { passive: true });
        button.addEventListener('touchstart', warmTarget, { passive: true });
        button.addEventListener('click', onClick);
        this.destroyCallbacks.push(() => button.removeEventListener('pointerenter', warmTarget));
        this.destroyCallbacks.push(() => button.removeEventListener('touchstart', warmTarget));
        this.destroyCallbacks.push(() => button.removeEventListener('click', onClick));
      });

      if (this.playPauseButton) {
        const onPlayPause = () => {
          if (this.userPaused || !this.classList.contains('is-playing')) {
            this.userPaused = false;
            this.autoplayEnabled = !this.reduceMotion.matches;
            this.resumePlayback({ restartIfNeeded: true });
          } else {
            this.userPaused = true;
            this.pausePlayback();
          }
          this.updatePlayPauseButton();
        };
        this.playPauseButton.addEventListener('click', onPlayPause);
        this.destroyCallbacks.push(() => this.playPauseButton.removeEventListener('click', onPlayPause));
      }

      this.querySelectorAll('[data-video-fallback-play]').forEach((button) => {
        const onManualPlay = () => this.handleManualPlay(button);
        button.addEventListener('click', onManualPlay);
        this.destroyCallbacks.push(() => button.removeEventListener('click', onManualPlay));
      });

      const swipeSurface = this.viewport || this;
      const interactiveSelector = 'a, button, input, select, textarea, [contenteditable="true"]';

      const resetPointerGesture = (event) => {
        const gesture = this.pointerGesture;
        if (!gesture || (event && event.pointerId !== gesture.pointerId)) return null;

        this.pointerGesture = null;
        this.classList.remove('is-swiping');

        if (swipeSurface.hasPointerCapture?.(gesture.pointerId)) {
          try {
            swipeSurface.releasePointerCapture(gesture.pointerId);
          } catch (error) {
            // The browser may release capture when native vertical scrolling wins.
          }
        }

        return gesture;
      };

      const onPointerDown = (event) => {
        if (this.slides.length < 2 || !event.isPrimary) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest(interactiveSelector)) return;

        this.pointerGesture = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          lastX: event.clientX,
          lastY: event.clientY,
          startedAt: performance.now(),
          axis: null,
          warmed: false,
        };

        try {
          swipeSurface.setPointerCapture?.(event.pointerId);
        } catch (error) {
          // Pointer capture is an enhancement; swipe still works without it.
        }
      };

      const onPointerMove = (event) => {
        const gesture = this.pointerGesture;
        if (!gesture || event.pointerId !== gesture.pointerId) return;

        gesture.lastX = event.clientX;
        gesture.lastY = event.clientY;
        const deltaX = gesture.lastX - gesture.startX;
        const deltaY = gesture.lastY - gesture.startY;
        const distanceX = Math.abs(deltaX);
        const distanceY = Math.abs(deltaY);

        if (!gesture.axis && Math.max(distanceX, distanceY) >= 10) {
          gesture.axis = distanceX > distanceY * 1.12 ? 'horizontal' : 'vertical';
        }

        if (gesture.axis !== 'horizontal') return;

        event.preventDefault();
        this.classList.add('is-swiping');

        if (!gesture.warmed) {
          this.warmSlide(deltaX > 0 ? this.getPreviousIndex() : this.getNextIndex());
          gesture.warmed = true;
        }
      };

      const onPointerUp = (event) => {
        const gesture = this.pointerGesture;
        if (!gesture || event.pointerId !== gesture.pointerId) return;

        gesture.lastX = event.clientX;
        gesture.lastY = event.clientY;
        const completedGesture = resetPointerGesture(event);
        if (!completedGesture || completedGesture.axis !== 'horizontal') return;

        const deltaX = completedGesture.lastX - completedGesture.startX;
        const deltaY = completedGesture.lastY - completedGesture.startY;
        const distanceX = Math.abs(deltaX);
        const elapsed = Math.max(performance.now() - completedGesture.startedAt, 1);
        const velocity = distanceX / elapsed;
        const width = swipeSurface.clientWidth || window.innerWidth || 390;
        const distanceThreshold = Math.min(72, Math.max(38, width * 0.12));
        const hasHorizontalIntent = distanceX > Math.abs(deltaY) * 1.12;
        const isCommittedSwipe = distanceX >= distanceThreshold || (distanceX >= 24 && velocity >= 0.34);

        if (!hasHorizontalIntent || !isCommittedSwipe) return;
        if (deltaX > 0) onPrev();
        else onNext();
      };

      const onPointerCancel = (event) => resetPointerGesture(event);

      swipeSurface.addEventListener('pointerdown', onPointerDown, { passive: true });
      swipeSurface.addEventListener('pointermove', onPointerMove, { passive: false });
      swipeSurface.addEventListener('pointerup', onPointerUp, { passive: true });
      swipeSurface.addEventListener('pointercancel', onPointerCancel, { passive: true });
      this.destroyCallbacks.push(() => swipeSurface.removeEventListener('pointerdown', onPointerDown));
      this.destroyCallbacks.push(() => swipeSurface.removeEventListener('pointermove', onPointerMove));
      this.destroyCallbacks.push(() => swipeSurface.removeEventListener('pointerup', onPointerUp));
      this.destroyCallbacks.push(() => swipeSurface.removeEventListener('pointercancel', onPointerCancel));

      const onKeydown = (event) => {
        if (event.key === 'ArrowLeft') onPrev();
        if (event.key === 'ArrowRight') onNext();
      };
      this.addEventListener('keydown', onKeydown);
      this.destroyCallbacks.push(() => this.removeEventListener('keydown', onKeydown));

      if (this.pauseOnHover) {
        const onMouseEnter = () => {
          this.hoverPaused = true;
          this.pausePlayback();
        };
        const onMouseLeave = () => {
          this.hoverPaused = false;
          this.resumePlayback();
        };
        this.addEventListener('mouseenter', onMouseEnter);
        this.addEventListener('mouseleave', onMouseLeave);
        this.destroyCallbacks.push(() => this.removeEventListener('mouseenter', onMouseEnter));
        this.destroyCallbacks.push(() => this.removeEventListener('mouseleave', onMouseLeave));
      }

      const onBreakpointChange = () => {
        this.stopPlayback({ resetProgress: true, resetMedia: false });
        this.pauseAllVideos();
        this.activateSlide(this.index, { announce: false });
        if (this.shouldRun()) this.startCurrentPlayback({ resetMedia: false });
      };

      if (this.isMobile.addEventListener) {
        this.isMobile.addEventListener('change', onBreakpointChange);
        this.destroyCallbacks.push(() => this.isMobile.removeEventListener('change', onBreakpointChange));
      } else {
        this.isMobile.addListener(onBreakpointChange);
        this.destroyCallbacks.push(() => this.isMobile.removeListener(onBreakpointChange));
      }

      const onVisibilityChange = () => {
        if (document.hidden) this.pausePlayback();
        else this.resumePlayback({ restartIfNeeded: true });
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      this.destroyCallbacks.push(() => document.removeEventListener('visibilitychange', onVisibilityChange));

      const onResize = () => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.updateAdaptiveHeight(this.slides[this.index]), 80);
      };
      window.addEventListener('resize', onResize, { passive: true });
      this.destroyCallbacks.push(() => window.removeEventListener('resize', onResize));

      const onBlockSelect = (event) => {
        const slide = event.target.closest('[data-hero-slide]');
        if (!slide || !this.contains(slide)) return;
        this.userPaused = true;
        this.goTo(Number(slide.dataset.slideIndex), { announce: false, restart: false, resetMedia: true });
        this.pausePlayback();
        this.updatePlayPauseButton();
      };

      const onBlockDeselect = () => {
        this.userPaused = false;
        this.resumePlayback({ restartIfNeeded: true });
      };

      this.addEventListener('shopify:block:select', onBlockSelect);
      this.addEventListener('shopify:block:deselect', onBlockDeselect);
      this.destroyCallbacks.push(() => this.removeEventListener('shopify:block:select', onBlockSelect));
      this.destroyCallbacks.push(() => this.removeEventListener('shopify:block:deselect', onBlockDeselect));
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.intersectionObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.inViewport = Boolean(entry?.isIntersecting && entry.intersectionRatio > 0.08);
        if (!this.inViewport) this.pausePlayback();
        else {
          this.hydrateSlideMedia(this.slides[this.index], { priority: 'high' });
          this.resumePlayback({ restartIfNeeded: true });
        }
      }, { threshold: [0, 0.08, 0.5] });
      this.intersectionObserver.observe(this);
    }

    isNearViewport(buffer = 80) {
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      return rect.bottom > -buffer && rect.top < viewportHeight + buffer;
    }

    shouldRun() {
      return Boolean(
        this.autoplayEnabled &&
        !this.userPaused &&
        !this.hoverPaused &&
        this.inViewport &&
        !document.hidden
      );
    }

    getPreviousIndex() {
      return (this.index - 1 + this.slides.length) % this.slides.length;
    }

    getNextIndex() {
      return (this.index + 1) % this.slides.length;
    }

    getVisibleMediaSource(slide) {
      if (!slide) return null;
      return slide.querySelector(
        this.isMobile.matches
          ? '.cahve-hero__media-source--mobile'
          : '.cahve-hero__media-source--desktop'
      );
    }

    getVisibleVideo(slide) {
      return this.getVisibleMediaSource(slide)?.querySelector('[data-hero-video]') || null;
    }

    getHeightMode() {
      return this.isMobile.matches ? this.mobileHeightMode : this.desktopHeightMode;
    }

    activateSlide(index, { announce = false } = {}) {
      const normalizedIndex = (index + this.slides.length) % this.slides.length;
      this.index = normalizedIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      if (this.transition === 'slide' && this.track) {
        this.track.style.transform = `translate3d(-${normalizedIndex * 100}%, 0, 0)`;
      }

      this.navButtons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-current', buttonIndex === normalizedIndex ? 'true' : 'false');
      });

      const activeSlide = this.slides[normalizedIndex];
      if (this.inViewport || this.isNearViewport()) {
        this.hydrateSlideMedia(activeSlide, { priority: 'high' });
      }
      this.updateAdaptiveHeight(activeSlide);

      if (announce && this.status) {
        this.status.textContent = `Slide ${normalizedIndex + 1} of ${this.slides.length}`;
      }

      this.updatePlayPauseButton();
    }

    goTo(nextIndex, options = {}) {
      const { announce = false, restart = false, resetMedia = true } = options;
      if (!this.slides.length || Number.isNaN(nextIndex)) return;

      this.stopPlayback({ resetProgress: true, resetMedia });
      this.activateSlide(nextIndex, { announce });
      if (restart && this.shouldRun()) this.startCurrentPlayback({ resetMedia: true });
    }

    hydrateSlideMedia(slide, { priority = 'normal' } = {}) {
      const source = this.getVisibleMediaSource(slide);
      if (!source) return;
      const image = source.querySelector('[data-hero-image]');
      const video = source.querySelector('[data-hero-video]');
      if (image) this.hydrateImage(image, priority);
      if (video) this.hydrateVideo(video, { priority });
    }

    hydrateImage(image, priority = 'normal') {
      if (!image || image.dataset.hydrated === 'true') return;
      if (image.dataset.srcset) image.srcset = image.dataset.srcset;
      if (image.dataset.sizes) image.sizes = image.dataset.sizes;
      if (image.dataset.src) image.src = image.dataset.src;
      image.loading = priority === 'high' ? 'eager' : 'lazy';
      image.fetchPriority = priority === 'high' ? 'high' : 'auto';
      image.dataset.hydrated = 'true';
    }

    hydrateVideo(video, { priority = 'normal' } = {}) {
      if (!video) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      if (priority === 'high') {
        video.autoplay = true;
        video.setAttribute('autoplay', '');
      } else {
        video.autoplay = false;
        video.removeAttribute('autoplay');
      }

      if (video.dataset.poster && !video.poster) video.poster = video.dataset.poster;

      const desiredPreload = priority === 'high' || priority === 'next' ? 'auto' : 'metadata';
      if (video.preload !== desiredPreload) video.preload = desiredPreload;

      if (video.dataset.hydrated !== 'true') {
        video.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        if (video.dataset.src && !video.src) video.src = video.dataset.src;
        video.dataset.hydrated = 'true';
      }

      if (video.dataset.listenersBound !== 'true') {
        const onMetadata = () => {
          const slide = video.closest('[data-hero-slide]');
          if (slide?.classList.contains('is-active')) this.updateAdaptiveHeight(slide, video);
        };
        const onCanPlay = () => video.classList.add('is-ready');
        const onPlaying = () => {
          video.classList.add('is-ready');
          video.classList.remove('is-buffering');
          const fallbackButton = video.parentElement?.querySelector('[data-video-fallback-play]');
          if (fallbackButton) fallbackButton.hidden = true;
          if (video === this.getVisibleVideo(this.slides[this.index])) this.scheduleNextSlidePreload();
        };
        const onWaiting = () => video.classList.add('is-buffering');
        const onEnded = () => video.classList.remove('is-buffering');

        video.addEventListener('loadedmetadata', onMetadata);
        video.addEventListener('durationchange', onMetadata);
        video.addEventListener('canplay', onCanPlay);
        video.addEventListener('playing', onPlaying);
        video.addEventListener('waiting', onWaiting);
        video.addEventListener('stalled', onWaiting);
        video.addEventListener('ended', onEnded);
        video.dataset.listenersBound = 'true';
        this.destroyCallbacks.push(() => video.removeEventListener('loadedmetadata', onMetadata));
        this.destroyCallbacks.push(() => video.removeEventListener('durationchange', onMetadata));
        this.destroyCallbacks.push(() => video.removeEventListener('canplay', onCanPlay));
        this.destroyCallbacks.push(() => video.removeEventListener('playing', onPlaying));
        this.destroyCallbacks.push(() => video.removeEventListener('waiting', onWaiting));
        this.destroyCallbacks.push(() => video.removeEventListener('stalled', onWaiting));
        this.destroyCallbacks.push(() => video.removeEventListener('ended', onEnded));
      }

      if ((priority === 'high' || priority === 'next') && video.readyState < 2 && video.networkState !== 2) {
        try { video.load(); } catch (error) { /* Browser controls media loading. */ }
      }
    }

    warmSlide(index) {
      if (!Number.isFinite(index) || !this.slides[index]) return;
      this.hydrateSlideMedia(this.slides[index], { priority: 'next' });
    }

    scheduleNextSlidePreload() {
      if (this.slides.length < 2) return;
      this.cancelPreload();

      const preload = () => {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        const saveData = Boolean(connection?.saveData);
        const verySlow = /(^|-)2g$/.test(connection?.effectiveType || '');
        const priority = saveData || verySlow ? 'normal' : 'next';
        this.hydrateSlideMedia(this.slides[this.getNextIndex()], { priority });
      };

      if ('requestIdleCallback' in window) {
        this.preloadHandle = window.requestIdleCallback(preload, { timeout: 1200 });
      } else {
        this.preloadHandle = window.setTimeout(preload, 350);
      }
    }

    cancelPreload() {
      if (this.preloadHandle == null) return;
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.preloadHandle);
      else window.clearTimeout(this.preloadHandle);
      this.preloadHandle = null;
    }

    updateAdaptiveHeight(slide, knownVideo = null) {
      if (!this.viewport || !slide) return;
      if (this.getHeightMode() !== 'adapt') {
        this.viewport.style.removeProperty('--cahve-adaptive-height');
        return;
      }

      const ratioAttribute = this.isMobile.matches
        ? slide.dataset.mobileAspectRatio
        : slide.dataset.desktopAspectRatio;
      let ratio = Number(ratioAttribute) || 1.7777778;
      const video = knownVideo || this.getVisibleVideo(slide);

      if (video?.videoWidth && video?.videoHeight) ratio = video.videoWidth / video.videoHeight;
      if (!Number.isFinite(ratio) || ratio <= 0) ratio = 1.7777778;

      const width = this.viewport.clientWidth || this.clientWidth;
      if (!width) return;
      this.viewport.style.setProperty('--cahve-adaptive-height', `${Math.max(1, Math.round(width / ratio))}px`);
    }

    startCurrentPlayback({ resetMedia = false } = {}) {
      this.clearSchedule({ resetProgress: true });
      if (!this.shouldRun()) {
        this.classList.remove('is-playing');
        this.updatePlayPauseButton();
        return;
      }

      const slide = this.slides[this.index];
      this.hydrateSlideMedia(slide, { priority: 'high' });
      const video = this.getVisibleVideo(slide);
      this.classList.add('is-playing');

      if (video) {
        if (resetMedia) this.resetVideo(video);
        const timing = slide.dataset.videoTiming || 'end';

        if (timing === 'end') {
          video.loop = false;
          video.removeAttribute('loop');
          this.currentSchedule = { type: 'video', video };
          this.attachVideoEnded(video);
          // Defer the progress loop until the video is confirmed playing so the ring
          // begins at exactly the same moment the video starts emitting frames.
          this.startVideoProgressWhenPlaying(video);
          this.playVideoWithRetry(video).then(() => {
            this.scheduleNextSlidePreload();
          }).catch(() => {
            this.startTimedSchedule(this.getVideoFallbackDuration(slide), 0, video);
          });
        } else {
          const duration = this.getVideoFallbackDuration(slide);
          this.startTimedSchedule(duration, 0, video);
          this.playVideoWithRetry(video).then(() => this.scheduleNextSlidePreload()).catch(() => {});
        }
      } else if (this.slides.length > 1) {
        const duration = Math.max(Number(slide.dataset.imageDuration) || this.defaultInterval, MIN_DURATION_MS);
        this.startTimedSchedule(duration, 0, null);
        this.scheduleNextSlidePreload();
      } else {
        this.updateProgress(0);
      }

      this.updatePlayPauseButton();
    }

    getVideoFallbackDuration(slide) {
      return Math.max(Number(slide?.dataset.videoFixedDuration) || this.defaultInterval, MIN_DURATION_MS);
    }

    playVideo(video) {
      if (!video) return Promise.reject(new Error('Video unavailable'));
      this.hydrateVideo(video, { priority: 'high' });
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.autoplay = true;
      video.setAttribute('autoplay', '');
      const fallbackButton = video.parentElement?.querySelector('[data-video-fallback-play]');
      const result = video.play();

      if (!result || typeof result.then !== 'function') {
        if (fallbackButton) fallbackButton.hidden = true;
        return Promise.resolve();
      }

      return result.then(() => {
        if (fallbackButton) fallbackButton.hidden = true;
      }).catch((error) => {
        if (fallbackButton && error?.name === 'NotAllowedError') fallbackButton.hidden = false;
        throw error;
      });
    }

    playVideoWithRetry(video) {
      return this.playVideo(video).catch((firstError) => {
        if (firstError?.name === 'NotAllowedError' || firstError?.name === 'SecurityError') {
          const fallbackButton = video.parentElement?.querySelector('[data-video-fallback-play]');
          if (fallbackButton) fallbackButton.hidden = false;
          throw firstError;
        }

        return new Promise((resolve, reject) => {
          let settled = false;
          const finish = (callback, value) => {
            if (settled) return;
            settled = true;
            video.removeEventListener('canplay', retry);
            window.clearTimeout(timeout);
            callback(value);
          };
          const retry = () => {
            this.playVideo(video).then(
              () => finish(resolve),
              (error) => finish(reject, error)
            );
          };
          const timeout = window.setTimeout(() => finish(reject, firstError), 1800);
          video.addEventListener('canplay', retry, { once: true });
        });
      });
    }

    handleManualPlay(button) {
      const source = button.closest('.cahve-hero__media-source');
      const video = source?.querySelector('[data-hero-video]');
      const slide = button.closest('[data-hero-slide]');
      if (!video || !slide) return;

      this.autoplayEnabled = true;
      this.userPaused = false;
      this.hoverPaused = false;
      this.hydrateVideo(video, { priority: 'high' });

      this.playVideo(video).then(() => {
        button.hidden = true;
        if (!slide.classList.contains('is-active')) return;
        this.classList.add('is-playing');

        if ((slide.dataset.videoTiming || 'end') === 'end') {
          this.clearTimedSchedule();
          this.currentSchedule = { type: 'video', video };
          this.attachVideoEnded(video);
          // play() has already resolved here so video.paused === false;
          // startVideoProgressWhenPlaying will begin the loop immediately.
          this.startVideoProgressWhenPlaying(video);
        }
        this.updatePlayPauseButton();
      }).catch(() => {
        button.hidden = false;
      });
    }

    attachVideoEnded(video) {
      this.detachVideoEnded();
      this.currentEndedVideo = video;
      this.currentEndedHandler = () => {
        if (video !== this.getVisibleVideo(this.slides[this.index])) return;

        // The native ended event is the single source of truth. Complete the
        // active ring at the exact media endpoint and switch slides in the
        // same task — there is deliberately no artificial hold or delay.
        this.cancelProgressLoop();
        this.updateProgress(1);
        this.advanceAfterCurrentSlide();
      };
      video.addEventListener('ended', this.currentEndedHandler, { once: true });
    }

    detachVideoEnded() {
      if (this.currentEndedVideo && this.currentEndedHandler) {
        this.currentEndedVideo.removeEventListener('ended', this.currentEndedHandler);
      }
      this.currentEndedVideo = null;
      this.currentEndedHandler = null;
    }

    startTimedSchedule(duration, elapsed = 0, video = null) {
      this.clearTimedSchedule();
      const safeDuration = Math.max(Number(duration) || this.defaultInterval, MIN_DURATION_MS);
      const safeElapsed = Math.min(Math.max(Number(elapsed) || 0, 0), safeDuration - 1);

      this.currentSchedule = {
        type: 'timed',
        duration: safeDuration,
        elapsed: safeElapsed,
        startedAt: performance.now(),
        video
      };

      this.timer = window.setTimeout(() => this.advanceAfterCurrentSlide(), safeDuration - safeElapsed);
      this.startTimedProgress();
    }

    clearTimedSchedule() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    advanceAfterCurrentSlide() {
      if (!this.shouldRun()) return;
      if (this.slides.length < 2) {
        const video = this.getVisibleVideo(this.slides[this.index]);
        if (video) {
          this.resetVideo(video);
          this.currentSchedule = { type: 'video', video };
          this.attachVideoEnded(video);
          this.startVideoProgressWhenPlaying(video);
          this.playVideoWithRetry(video).catch(() => {});
        }
        return;
      }
      this.goTo(this.getNextIndex(), { announce: false, restart: true, resetMedia: true });
    }

    getVideoDurationSeconds(video) {
      if (!video) return 0;

      // Prefer the duration reported by the active browser media pipeline.
      // Do not take Math.max(duration, seekableEnd): on Safari/HLS the seekable
      // window can extend beyond the presentation duration and desynchronise
      // the ring from the native ended event.
      const nativeSeconds = Number(video.duration);
      if (Number.isFinite(nativeSeconds) && nativeSeconds > 0) return nativeSeconds;

      // Safari can briefly expose Infinity for streamed media. In that narrow
      // state, use the final seekable timestamp until a finite duration arrives.
      try {
        if (video.seekable?.length) {
          const seekableEnd = Number(video.seekable.end(video.seekable.length - 1));
          if (Number.isFinite(seekableEnd) && seekableEnd > 0) return seekableEnd;
        }
      } catch (error) {
        // The active media implementation controls seekable ranges.
      }

      // Shopify's stored duration is only a pre-metadata visual fallback.
      // NOTE: Shopify exposes video.duration in SECONDS (not milliseconds). The Liquid
      // template writes it directly as `{{ desktop_video_duration }}` without a | times: 1000
      // filter, so the data attribute value is already in seconds. Do not divide by 1000.
      const slide = video.closest('[data-hero-slide]');
      const liquidSeconds = this.isMobile.matches
        ? Number(slide?.dataset.mobileVideoDuration)
        : Number(slide?.dataset.desktopVideoDuration);
      return Number.isFinite(liquidSeconds) && liquidSeconds > 0 ? liquidSeconds : 0;
    }

    /**
     * Start the progress loop only when the video is confirmed playing, so the
     * ring begins at the same moment the video starts emitting frames.
     */
    startVideoProgressWhenPlaying(video) {
      this.cancelProgressLoop();

      if (!video.paused && video.readyState >= 3) {
        this.startVideoProgress(video);
        return;
      }

      const onPlaying = () => {
        this._pendingPlayingVideo = null;
        this._pendingPlayingHandler = null;
        if (
          this.currentSchedule?.type !== 'video' ||
          this.currentSchedule.video !== video
        ) return;
        this.startVideoProgress(video);
      };
      this._pendingPlayingVideo = video;
      this._pendingPlayingHandler = onPlaying;
      video.addEventListener('playing', onPlaying, { once: true });
    }

    /**
     * Drive the ring directly from the video's own playback clock:
     *
     *   progress = video.currentTime / video.duration
     *
     * A single requestAnimationFrame loop is used (not requestVideoFrameCallback)
     * because rAF behaves identically across iOS Safari, macOS Safari, Chrome and
     * Chromium, and reading currentTime at 60 fps is more than precise enough for
     * a progress ring. One loop, one code path, no browser-specific behaviour.
     */
    startVideoProgress(video) {
      this.cancelProgressLoop();

      // The loop is capped just below 1 so the ring can never visually complete
      // before the native 'ended' event. Only the ended handler writes exactly 1.
      const MAX_LOOP_PROGRESS = 1 - 1e-4;

      const tick = () => {
        const schedule = this.currentSchedule;
        if (schedule?.type !== 'video' || schedule.video !== video) return;
        if (video.ended) return; // ended handler writes the final 1.0

        const duration = this.getVideoDurationSeconds(video);
        if (duration > 0) {
          const currentTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
          const progress = currentTime / duration;
          this.updateProgress(Math.min(Math.max(progress, 0), MAX_LOOP_PROGRESS));
        } else {
          // No usable duration yet (metadata still loading) — hold the ring at 0.
          this.updateProgress(0);
        }

        if (
          this.currentSchedule === schedule &&
          this.classList.contains('is-playing') &&
          !video.ended
        ) {
          this.progressFrame = window.requestAnimationFrame(tick);
        }
      };

      tick();
    }


    startTimedProgress() {
      this.cancelProgressLoop();
      const tick = () => {
        const schedule = this.currentSchedule;
        if (!schedule || schedule.type !== 'timed') return;
        const elapsed = schedule.elapsed + (performance.now() - schedule.startedAt);
        this.updateProgress(Math.min(Math.max(elapsed / schedule.duration, 0), 1));
        if (this.currentSchedule?.type === 'timed' && this.classList.contains('is-playing')) {
          this.progressFrame = window.requestAnimationFrame(tick);
        }
      };
      this.progressFrame = window.requestAnimationFrame(tick);
    }

    cancelProgressLoop() {
      if (this.progressFrame) {
        window.cancelAnimationFrame(this.progressFrame);
        this.progressFrame = null;
      }
      const activeVideo = this.currentSchedule?.type === 'video' ? this.currentSchedule.video : null;
      if (this.videoFrameRequest != null && activeVideo && typeof activeVideo.cancelVideoFrameCallback === 'function') {
        activeVideo.cancelVideoFrameCallback(this.videoFrameRequest);
      }
      this.videoFrameRequest = null;

      // Cancel any pending 'playing' event waiter installed by startVideoProgressWhenPlaying.
      if (this._pendingPlayingVideo && this._pendingPlayingHandler) {
        this._pendingPlayingVideo.removeEventListener('playing', this._pendingPlayingHandler);
        this._pendingPlayingVideo = null;
        this._pendingPlayingHandler = null;
      }
    }

    /**
     * Measure the ring circle's real geometric length once and cache it.
     *
     * The SVG must NOT rely on the pathLength attribute: it is inconsistently
     * honoured (in this theme it was being ignored entirely, leaving a
     * stroke-dasharray of 100 against a real circumference of ~294.8, so the
     * dash pattern repeated around the circle and the ring appeared to complete
     * roughly a third of the way through playback).
     *
     * Measuring with getTotalLength() and writing stroke-dasharray from it makes
     * the arc geometry exact regardless of radius, stroke width or breakpoint.
     */
    getRingCircumference(ring) {
      const cached = Number(ring.dataset.ringLength);
      if (Number.isFinite(cached) && cached > 0) return cached;

      let length = 0;
      try {
        if (typeof ring.getTotalLength === 'function') length = ring.getTotalLength();
      } catch (error) {
        length = 0;
      }

      if (Number.isFinite(length) && length > 0) {
        ring.dataset.ringLength = String(length);
        ring.style.strokeDasharray = String(length);
        return length;
      }

      // Not measurable yet (element not rendered). Use the geometric value for
      // r=47 this frame, but do not cache it so we can measure properly later.
      const fallback = 2 * Math.PI * 47;
      ring.style.strokeDasharray = String(fallback);
      return fallback;
    }

    updateProgress(progress) {
      this.navButtons.forEach((button, index) => {
        const isActive = index === this.index;
        const ring = button.querySelector('[data-carousel-ring]');
        const bar = button.querySelector('[data-carousel-progress]');
        const dotProgress = button.querySelector('[data-carousel-dot-progress]');
        const value = isActive ? progress : 0;
        if (ring) {
          const circumference = this.getRingCircumference(ring);
          ring.style.strokeDashoffset = String(circumference * (1 - value));
        }
        if (bar) bar.style.transform = `scaleX(${value})`;
        if (dotProgress) dotProgress.style.transform = `scaleX(${value})`;
      });
    }

    pausePlayback() {
      if (!this.classList.contains('is-playing')) return;
      if (this.currentSchedule?.type === 'timed') {
        this.currentSchedule.elapsed += performance.now() - this.currentSchedule.startedAt;
        this.currentSchedule.startedAt = performance.now();
      }
      this.clearTimedSchedule();
      this.cancelProgressLoop();
      const video = this.getVisibleVideo(this.slides[this.index]);
      if (video && !video.paused) video.pause();
      this.classList.remove('is-playing');
      this.updatePlayPauseButton();
    }

    resumePlayback({ restartIfNeeded = false } = {}) {
      if (this.classList.contains('is-playing')) return;
      if (!this.shouldRun()) {
        this.classList.remove('is-playing');
        this.updatePlayPauseButton();
        return;
      }

      const schedule = this.currentSchedule;
      const video = this.getVisibleVideo(this.slides[this.index]);
      this.classList.add('is-playing');

      if (!schedule && restartIfNeeded) {
        this.startCurrentPlayback({ resetMedia: false });
        return;
      }

      if (schedule?.type === 'timed') {
        schedule.startedAt = performance.now();
        this.timer = window.setTimeout(
          () => this.advanceAfterCurrentSlide(),
          Math.max(1, schedule.duration - schedule.elapsed)
        );
        if (video) this.playVideoWithRetry(video).catch(() => {});
        this.startTimedProgress();
      } else if (schedule?.type === 'video' && video) {
        this.attachVideoEnded(video);
        this.startVideoProgressWhenPlaying(video);
        this.playVideoWithRetry(video).then(() => this.scheduleNextSlidePreload()).catch(() => {
          this.startTimedSchedule(this.getVideoFallbackDuration(this.slides[this.index]), 0, video);
        });
      } else if (!schedule) {
        this.startCurrentPlayback({ resetMedia: false });
      }

      this.updatePlayPauseButton();
    }

    clearSchedule({ resetProgress = false } = {}) {
      this.clearTimedSchedule();
      this.detachVideoEnded();
      this.cancelProgressLoop();
      this.currentSchedule = null;
      if (resetProgress) this.updateProgress(0);
    }

    stopPlayback({ resetProgress = false, resetMedia = false } = {}) {
      this.clearSchedule({ resetProgress });
      const activeVideo = this.getVisibleVideo(this.slides[this.index]);
      if (activeVideo) {
        activeVideo.pause();
        if (resetMedia) this.resetVideo(activeVideo);
      }
      this.classList.remove('is-playing');
    }

    resetVideo(video) {
      if (!video) return;
      try {
        if (video.readyState >= 1) video.currentTime = 0;
      } catch (error) {
        // Some streaming implementations reject seeking before metadata is ready.
      }
    }

    pauseAllVideos() {
      this.querySelectorAll('[data-hero-video]').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }

    updatePlayPauseButton() {
      if (!this.playPauseButton) return;
      const isPlaying = this.classList.contains('is-playing') && !this.userPaused;
      this.playPauseButton.setAttribute('aria-label', isPlaying ? 'Pause carousel' : 'Play carousel');
      this.playPauseButton.setAttribute('aria-pressed', isPlaying ? 'false' : 'true');
    }
  }

  customElements.define('cahve-hero-carousel', CahveHeroCarousel);
})();
